import { Box, Grid } from "@mui/material";
import React from "react";
import { Button, ButtonVariantOption } from "../Button";
import { StyledDataGrid } from "../DataGrid/ChibanKaokuNumberSearchStyleDataGrid";
import { type GridSelectionModel, type GridColDef } from "@mui/x-data-grid-pro";
import {
  type ChibanInputRow,
  type KaokuNumberInputRow,
} from "@/types/kaokuNumber";
import { toast } from "react-toastify";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { NYHalfWidthNumberToFillWidthNumber } from "@/utils/utils";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useIsMobile } from "@/hooks/useIsMobile";
import { SelectableCardList } from "./SelectableCardList";

interface Props {
  title: string;
  buttonLabel: string;
  dataType: "chiban" | "kaokuNumber";
  count: number;
  handleRowSelectionChange: (newSelectionModel: GridSelectionModel) => void;
  loading: boolean;
  maxRequestCount: number;
  rows: ChibanInputRow[] | KaokuNumberInputRow[];
  selectedRowIds: GridSelectionModel;
  setOpenSearchModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  columns?: GridColDef[];
  height?: number;
}

// デフォルトカラム定義
const defaultChibanColumns: GridColDef[] = [
  {
    field: "prefectureName",
    headerName: "都道府県",
    width: 150,
    sortable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "location",
    headerName: "所在",
    width: 400,
    sortable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "chiban",
    headerName: "地番",
    width: 240,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => {
      return NYHalfWidthNumberToFillWidthNumber(params.value as string);
    },
  },
  {
    field: "hitCount",
    headerName: "家屋番号ヒット件数",
    width: 240,
    sortable: false,
    align: "center",
    headerAlign: "center",
    cellClassName: (params) => {
      const value = params.value as number | undefined;
      if (value === undefined || value === null) return "";
      // 0件の場合は赤色、1件以上の場合は青色
      return value === 0 ? "hit-count-cell-zero" : "hit-count-cell";
    },
    renderCell: (params) => {
      const value = params.value as number | undefined;
      if (value === undefined || value === null) return "";
      return `${formatNumberWithComma(value)}件`;
    },
  },
];

const defaultKaokuNumberColumns: GridColDef[] = [
  {
    field: "prefectureName",
    headerName: "都道府県",
    width: 150,
    sortable: false,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "location",
    headerName: "所在",
    width: 400,
    sortable: false,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "kaokuNumber",
    headerName: "家屋番号",
    width: 240,
    sortable: false,
    align: "left",
    headerAlign: "left",
    renderCell: (params) => {
      return NYHalfWidthNumberToFillWidthNumber(params.value as string);
    },
  },
  {
    field: "hitCount",
    headerName: "地番ヒット件数",
    width: 240,
    sortable: false,
    align: "center",
    headerAlign: "center",
    cellClassName: (params) => {
      const value = params.value as number | undefined;
      if (value === undefined || value === null) return "";
      // 0件の場合は赤色、1件以上の場合は青色
      return value === 0 ? "hit-count-cell-zero" : "hit-count-cell";
    },
    renderCell: (params) => {
      const value = params.value as number | undefined;
      if (value === undefined || value === null) return "";
      return `${formatNumberWithComma(value)}件`;
    },
  },
];

/**
 * 検索入力の表示領域（汎用版）
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const ViewAreaSearchInput: React.FC<Props> = (props) => {
  const {
    title,
    buttonLabel,
    dataType,
    count,
    handleRowSelectionChange,
    loading,
    maxRequestCount,
    rows,
    selectedRowIds,
    setOpenSearchModalFlag,
    columns,
    height = 300,
  } = props;

  const isMobile = useIsMobile();

  const getColumns = (): GridColDef[] => {
    if (columns) return columns;
    return dataType === "chiban"
      ? defaultChibanColumns
      : defaultKaokuNumberColumns;
  };

  const searchButton = (
    <Button
      label={buttonLabel}
      size={SizingWrapperStyle.INHERIT}
      variant={ButtonVariantOption.Outlined}
      disabled={loading}
      onClick={() => {
        if (count > maxRequestCount) {
          toast.error(
            `件数は${formatNumberWithComma(
              maxRequestCount
            )}件以下にしてください。`
          );
          return;
        }
        if (count === 0) {
          toast.error("検索対象を選択してください。");
          return;
        }
        setOpenSearchModalFlag(true);
      }}
    />
  );

  // SP は表をカードにし、ボタンは横いっぱいにせず右寄せ
  if (isMobile) {
    return (
      <>
        <SelectableCardList
          title={title}
          columns={getColumns()}
          rows={rows}
          selectedRowIds={selectedRowIds}
          handleRowSelectionChange={handleRowSelectionChange}
        />
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: 2 }}>
          <Box sx={{ width: "fit-content" }}>{searchButton}</Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ height }}>
        <StyledDataGrid
          rowHeight={36}
          headerHeight={40}
          disableColumnMenu
          disableSelectionOnClick
          checkboxSelection
          rows={rows}
          columns={getColumns()}
          selectionModel={selectedRowIds}
          onSelectionModelChange={handleRowSelectionChange}
        />
      </Box>
      <Grid container mt={1} mb={2} justifyContent="flex-end">
        <Grid item xs={12} sm={6} md={4} lg={3} mx={2}>
          {searchButton}
        </Grid>
      </Grid>
    </>
  );
};
